import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Award, Plus, Eye, Pencil, CheckCircle2, FileText, Trash2, X, Loader2,
  CalendarDays, ArrowLeft, AlertTriangle, FileSpreadsheet, Download,
  Check, Ban, Hourglass,
  // Fix N (2026-08-31): enviar al cliente, devolución del cliente y vista agrupada.
  Send, Undo2, Layers
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatUSD2, formatDate } from "../../utils/format";
import { fmtTC, useTipoCambio } from "../../utils/tipoCambio";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";

const opts = { withCredentials: true };

// Fix FG (2026-10-08): "La generación de los certificados...todo lo ligado a eso debe
// permitir la posibilidad de visualizar o cargar valores en DOLARES o GUARANIES" (CEN).
// Los montos se guardan en Gs.; si el contrato de la obra es en USD se muestran y se
// cargan en dólares al tipo de cambio del contrato (`moneda`/`tipo_cambio` del backend).
function monedaCert(info) {
  const tc = Number(info?.tipo_cambio) || 0;
  const usd = info?.moneda === "USD" && tc > 0;
  return {
    usd, tc,
    fmt: (v) => (usd ? formatUSD2((Number(v) || 0) / tc) : formatPYG(v)),
    sigla: usd ? "US$" : "Gs.",
    aGs: (v) => (usd ? Math.round((Number(v) || 0) * tc * 100) / 100 : Number(v) || 0),
    deGs: (v) => (usd ? Math.round(((Number(v) || 0) / tc) * 100) / 100 : Number(v) || 0),
  };
}

const ESTADO_BADGE = {
  // Fix EA (2026-09-23): emitida = ya está en Finanzas pendiente de cobro; pagada = el
  // cobro se registró en Tesorería → Cobranzas.
  emitida:  { label: "Enviada a Finanzas", cls: "bg-blue-100 text-blue-700" },
  pagada:   { label: "Cobrada", cls: "bg-emerald-100 text-emerald-700" },
  vencida:  { label: "Vencida",  cls: "bg-red-100 text-red-700" },
  anulada:  { label: "Anulada",  cls: "bg-gray-100 text-gray-500" },
  pendiente_aprobacion: { label: "Pend. Aprobación", cls: "bg-amber-100 text-amber-700" },
  rechazada: { label: "Rechazada", cls: "bg-red-100 text-red-600" },
  proyectado: { label: "Proyectado", cls: "bg-purple-100 text-purple-700" },
};

function origenInfo(item, adicionalesIds) {
  if (item.origen_tipo === "adicional") {
    const idx = (adicionalesIds || []).indexOf(item.origen_budget_id);
    const n = idx >= 0 ? idx + 1 : "?";
    return { key: item.origen_budget_id, label: `Adicional ${n}`, badgeCls: "bg-orange-100 text-orange-700" };
  }
  return { key: "base", label: "Base", badgeCls: "bg-emerald-100 text-emerald-700" };
}

async function downloadFile(url, filename) {
  const { data } = await axios.get(url, { ...opts, responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(href);
}

// ─── EDITOR ──────────────────────────────────────────────────────────────
function CertEditor({ obra, certId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [meta, setMeta] = useState({
    fecha_emision: new Date().toISOString().slice(0, 10),
    fecha_cierre: "", fecha_pago_estimada: "",
    anticipo_aplicado: 0, retencion_fondo_reparo: 0, multas: 0, observaciones: "",
    // Fix FA (2026-10-06): anticipo y retención en % del bruto o en Gs.
    anticipo_pct: 0, retencion_pct: 0, anticipo_modo: "pct", retencion_modo: "pct",
  });

  useEffect(() => {
    setLoading(true);
    const qs = certId ? `?cert_id=${certId}` : "";
    axios.get(`${API}/obras/${obra._id}/certificaciones/editor${qs}`, opts)
      .then(({ data }) => {
        setData(data);
        setRows((data.rows || []).map(r => ({ ...r, _pct: Number(r.porcentaje_avance) || 0 })));
        if (data.cert) {
          const m0 = monedaCert(data);   // Fix FG: los montos se cargan en la moneda del contrato
          setMeta({
            fecha_emision: data.cert.fecha_emision || new Date().toISOString().slice(0, 10),
            fecha_cierre: data.cert.fecha_cierre || "",
            fecha_pago_estimada: data.cert.fecha_pago_estimada || "",
            anticipo_aplicado: m0.deGs(data.cert.anticipo_aplicado || 0),
            retencion_fondo_reparo: m0.deGs(data.cert.retencion_fondo_reparo || 0),
            multas: m0.deGs(data.cert.multas || 0),
            observaciones: data.cert.observaciones || "",
            // Fix FA: con % guardado va en %; con monto y sin %, en Gs.
            anticipo_pct: data.cert.anticipo_pct || 0, retencion_pct: data.cert.retencion_pct || 0,
            anticipo_modo: data.cert.anticipo_pct > 0 || !(data.cert.anticipo_aplicado > 0) ? "pct" : "monto",
            retencion_modo: data.cert.retencion_pct > 0 || !(data.cert.retencion_fondo_reparo > 0) ? "pct" : "monto",
          });
        }
      })
      .catch(() => toast.error("Error cargando editor"))
      .finally(() => setLoading(false));
  }, [obra._id, certId]);

  const setPct = (idx, val) => {
    const v = Math.max(0, Math.min(100, Number(val) || 0));
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, _pct: v } : r));
  };

  const adicionalesIds = data?.adicionales_ids || [];
  const mon = monedaCert(data);   // Fix FG
  const filterOptions = useMemo(() => {
    const o = [["todos", "Todos"], ["base", "Base"]];
    (adicionalesIds || []).forEach((bid, i) => o.push([bid, `Adicional ${i + 1}`]));
    return o;
  }, [adicionalesIds]);

  const matchesFilter = (r) => {
    if (filter === "todos") return true;
    if (filter === "base") return r.origen_tipo !== "adicional";
    return r.origen_budget_id === filter;
  };

  // Cálculos en vivo
  const computed = useMemo(() => {
    let neto = 0; let over = false;
    const subt = {};
    const rowsOut = rows.map(r => {
      if (r.is_group) return { ...r, _cantCert: 0, _montoParcial: 0, _acum: 0 };
      const pct = Number(r._pct) || 0;
      const cantCert = (pct / 100) * Number(r.cantidad || 0);
      const montoParcial = (pct / 100) * Number(r.total_presupuesto || 0);
      const acum = Number(r.porcentaje_anterior || 0) + pct;
      if (acum > 100.01) over = true;
      neto += montoParcial;
      const info = origenInfo(r, adicionalesIds);
      subt[info.label] = (subt[info.label] || 0) + montoParcial;
      return { ...r, _cantCert: cantCert, _montoParcial: montoParcial, _acum: acum };
    });
    const iva = neto * 0.10;
    const bruto = neto + iva;
    // Fix FA (2026-10-06): en % se calcula sobre el bruto de la certificación.
    const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
    // Fix FG: los montos fijos se cargan en la moneda del contrato y se guardan en Gs.
    const m = monedaCert(data);
    const anticipo = r2(meta.anticipo_modo === "pct" ? r2(bruto) * (Number(meta.anticipo_pct) || 0) / 100 : m.aGs(meta.anticipo_aplicado));
    const retencion = r2(meta.retencion_modo === "pct" ? r2(bruto) * (Number(meta.retencion_pct) || 0) / 100 : m.aGs(meta.retencion_fondo_reparo));
    const multas = r2(m.aGs(meta.multas));
    const cobrable = bruto - anticipo - retencion - multas;
    return { rowsOut, neto, iva, bruto, cobrable, subt, over, anticipo, retencion, multas };
  }, [rows, meta, adicionalesIds, data]);

  // Fix N (2026-08-31): `borrador` decide si la certificación nace en estado
  // "proyectado" o directo en "emitida". El backend ya lo soportaba desde
  // siempre (certificaciones.py:243 y :400) pero el formulario nunca mandaba la
  // bandera, así que el estado "Proyectado" era inalcanzable desde la pantalla y
  // el endpoint /enviar quedaba muerto. Son los tres estados que pidió el cliente.
  const save = async (borrador = false) => {
    if (!borrador && computed.over) { toast.error("Hay ítems con % acumulado mayor a 100%. Corregilos antes de guardar."); return; }
    setSaving(true);
    const body = {
      fecha_emision: meta.fecha_emision || null,
      fecha_cierre: meta.fecha_cierre || null,
      fecha_pago_estimada: meta.fecha_pago_estimada || null,
      items: rows.filter(r => !r.is_group && Number(r._pct) > 0).map(r => ({ item_obra_idx: r.item_obra_idx, porcentaje_avance: Number(r._pct) })),
      anticipo_aplicado: computed.anticipo,   // Fix FA
      retencion_fondo_reparo: computed.retencion,
      anticipo_pct: meta.anticipo_modo === "pct" ? Number(meta.anticipo_pct) || 0 : 0,
      retencion_pct: meta.retencion_modo === "pct" ? Number(meta.retencion_pct) || 0 : 0,
      multas: computed.multas,   // Fix FG: en Gs.
      observaciones: meta.observaciones || "",
      guardar_borrador: !!borrador,
    };
    try {
      if (certId) await axios.put(`${API}/obras/${obra._id}/certificaciones/${certId}`, body, opts);
      else await axios.post(`${API}/obras/${obra._id}/certificaciones`, body, opts);
      toast.success(certId ? "Certificación actualizada" : (borrador ? "Borrador guardado" : "Certificación enviada a Finanzas"));
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error al guardar"));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>;

  return (
    <div className="space-y-4" data-testid="cert-editor">
      {/* Encabezado editor */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <button onClick={onClose} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 font-medium" data-testid="cert-editor-back">
          <ArrowLeft size={15} /> Volver a Certificaciones
        </button>
        <div className="text-xs text-gray-500">
          <span className="font-mono bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-lg">{data?.ots_numero}</span>
          <span className="ml-2">Certificación N° <strong>{data?.numero}</strong></span>
        </div>
      </div>
      {mon.usd && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="cert-editor-usd">
          Contrato en dólares: los montos se muestran y se cargan en US$ · cambio del contrato {fmtTC(mon.tc)}
        </div>
      )}

      {/* Datos cabecera */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Fecha emisión</label>
          <input type="date" value={meta.fecha_emision} onChange={e => setMeta(m => ({ ...m, fecha_emision: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="cert-fecha-emision" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Fecha cierre</label>
          <input type="date" value={meta.fecha_cierre} onChange={e => setMeta(m => ({ ...m, fecha_cierre: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="cert-fecha-cierre" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Fecha pago estimada</label>
          <input type="date" value={meta.fecha_pago_estimada} onChange={e => setMeta(m => ({ ...m, fecha_pago_estimada: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="cert-fecha-pago" /></div>
      </div>

      {/* Filtros por origen */}
      <div className="flex gap-2 flex-wrap">
        {filterOptions.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${filter === key ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-300 hover:border-[#EA580C]/40"}`} data-testid={`cert-filtro-${key}`}>{label}</button>
        ))}
      </div>

      {/* Tabla de ítems */}
      <div className="overflow-x-auto rounded-2xl border border-gray-200/60 bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left">
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide w-12">N°</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Descripción</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide w-14">Un.</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-20">Cant.</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-28">Total Ppto.</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide w-24">% Avance</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-20">Cant. cert.</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-28">Monto parcial</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-16">Ant.%</th>
              <th className="px-2 py-2.5 font-semibold text-gray-500 uppercase tracking-wide text-right w-16">Acum.%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {computed.rowsOut.map((r, idx) => {
              if (!matchesFilter(r)) return null;
              if (r.is_group) {
                return (
                  <tr key={idx} className="bg-[#EBF3FB]">
                    <td className="px-2 py-1.5 font-bold text-[#2E6DA4]">{r.numero}</td>
                    <td className="px-2 py-1.5 font-bold text-[#2E6DA4]" colSpan={9}>{r.descripcion}</td>
                  </tr>
                );
              }
              const over = r._acum > 100.01;
              return (
                <tr key={idx} className="hover:bg-gray-50/60" data-testid={`cert-item-row-${r.item_obra_idx}`}>
                  <td className="px-2 py-1.5 text-gray-400 font-mono">{r.numero}</td>
                  <td className="px-2 py-1.5 text-gray-800">{r.descripcion}</td>
                  <td className="px-2 py-1.5 text-gray-500">{r.unidad}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{Number(r.cantidad).toLocaleString("es-PY")}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{mon.fmt(r.total_presupuesto)}</td>
                  <td className="px-2 py-1.5">
                    <input type="number" min={0} max={100} value={r._pct}
                      onChange={e => setPct(idx, e.target.value)}
                      className={`w-16 border rounded-lg px-2 py-1 text-right text-xs focus:ring-2 ${over ? "border-red-400 ring-red-100 text-red-600" : "border-gray-300 focus:ring-[#EA580C]/20 focus:border-[#EA580C]"}`}
                      data-testid={`cert-pct-input-${r.item_obra_idx}`} />
                        {/* Fix N (2026-08-31): el backend calcula avance_sugerido_pct
                            a partir del estado de las tareas y lo manda en cada fila
                            (certificaciones.py:329-331); hasta ahora se descartaba.
                            Es una sugerencia: el número final lo carga el coordinador,
                            como pidió el cliente. Tocar el valor lo copia al campo. */}
                        {r.avance_sugerido_pct != null && Number(r.avance_sugerido_pct) !== Number(r._pct) && (
                          <button type="button" onClick={() => setPct(idx, r.avance_sugerido_pct)}
                            className="text-[10px] text-[#EA580C] hover:underline mt-0.5 block"
                            title="Calculado desde el avance de las tareas de la obra"
                            data-testid={`cert-sugerido-${r.item_obra_idx}`}>
                            sugerido: {r.avance_sugerido_pct}%
                          </button>
                        )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{r._cantCert.toLocaleString("es-PY", { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium text-gray-800">{mon.fmt(r._montoParcial)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-400">{Number(r.porcentaje_anterior).toFixed(0)}%</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${over ? "text-red-600" : "text-gray-700"}`} data-testid={`cert-acum-${r.item_obra_idx}`}>{r._acum.toFixed(0)}%</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-gray-200">
            {Object.entries(computed.subt).map(([label, monto]) => (
              <tr key={label} className="bg-gray-50/60"><td colSpan={6} className="px-2 py-1.5 text-right text-gray-500">Subtotal {label}</td><td colSpan={4} className="px-2 py-1.5 font-semibold text-gray-700 tabular-nums">{mon.fmt(monto)}</td></tr>
            ))}
            <tr className="bg-[#FFF7ED]"><td colSpan={6} className="px-2 py-2 text-right font-bold text-gray-700">Monto Neto de esta certificación</td><td colSpan={4} className="px-2 py-2 font-bold text-[#C2410C] tabular-nums" data-testid="cert-neto-foot">{mon.fmt(computed.neto)}</td></tr>
          </tfoot>
        </table>
      </div>

      {computed.over && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="cert-over-warning">
          <AlertTriangle size={16} /> Hay ítems cuyo % acumulado supera el 100%. Reducí el % de avance para poder guardar.
        </div>
      )}

      {/* Ajustes + totales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Ajustes</p>
          {/* Fix FA (2026-10-06): anticipo y retención en % del bruto o en Gs.; multas en Gs.
              Fix FG: en un contrato en USD, los montos fijos van en US$. */}
          {[["anticipo_aplicado", "Anticipo aplicado", "anticipo"], ["retencion_fondo_reparo", "Retención fondo de reparo", "retencion"], ["multas", "Multas", null]].map(([k, l, p]) => {
            const enPct = p && meta[`${p}_modo`] === "pct";
            const campo = enPct ? `${p}_pct` : k;
            return (
              <div key={k} className="flex items-center justify-between gap-3">
                <label className="text-sm text-gray-600">{l}{!p && mon.usd ? " (US$)" : ""}</label>
                <div className="flex items-center gap-1.5">
                  {p && (
                    <select value={meta[`${p}_modo`]} onChange={e => setMeta(m => ({ ...m, [`${p}_modo`]: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-sm bg-white" data-testid={`cert-${p}-modo`}>
                      <option value="pct">%</option>
                      <option value="monto">{mon.sigla}</option>
                    </select>
                  )}
                  <input type="number" min={0} max={enPct ? 100 : undefined} value={meta[campo]} onChange={e => setMeta(m => ({ ...m, [campo]: e.target.value }))} className={`${p ? "w-32" : "w-40"} border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-right`} data-testid={`cert-${campo}`} />
                </div>
              </div>
            );
          })}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones</label>
            <textarea value={meta.observaciones} onChange={e => setMeta(m => ({ ...m, observaciones: e.target.value }))} rows={2} className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="cert-observaciones" />
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Cálculo final</p>
          {[["Monto Neto", computed.neto], ["IVA (10%)", computed.iva], ["Monto Bruto", computed.bruto]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-sm"><span className="text-gray-500">{l}</span><span className="tabular-nums text-gray-800">{mon.fmt(v)}</span></div>
          ))}
          <div className="flex justify-between text-sm"><span className="text-gray-500">(-) Anticipo{meta.anticipo_modo === "pct" ? ` (${Number(meta.anticipo_pct) || 0}%)` : ""}</span><span className="tabular-nums text-gray-600" data-testid="cert-anticipo-monto">-{mon.fmt(computed.anticipo)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">(-) Retención{meta.retencion_modo === "pct" ? ` (${Number(meta.retencion_pct) || 0}%)` : ""}</span><span className="tabular-nums text-gray-600" data-testid="cert-retencion-monto">-{mon.fmt(computed.retencion)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">(-) Multas</span><span className="tabular-nums text-gray-600" data-testid="cert-multas-monto">-{mon.fmt(computed.multas)}</span></div>
          <div className="flex justify-between pt-2 border-t border-gray-200 text-base font-bold"><span className="text-gray-800">Monto Cobrable</span><span className="tabular-nums text-[#C2410C]" data-testid="cert-cobrable">{mon.fmt(computed.cobrable)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-5 py-2.5 text-sm font-medium">Cancelar</button>
        {/* Fix N (2026-08-31): el borrador NO valida el tope de 100%: se guarda
            como está y se valida recién al enviarlo, que es como lo pidió el
            cliente (se proyecta, después se confirma). */}
        {!certId && (
          <button onClick={() => save(true)} disabled={saving} className="border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-xl px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="cert-borrador-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Hourglass size={15} />} Guardar borrador
          </button>
        )}
        {/* Fix EA (2026-09-23): una certificación nueva o va como borrador (se edita y
            después se confirma el envío) o se confirma ya: pasa a Finanzas como pendiente. */}
        <button onClick={() => save(false)} disabled={saving || computed.over} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl px-6 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="cert-save-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} {certId ? "Guardar cambios" : "Guardar y enviar a Finanzas"}
        </button>
      </div>
    </div>
  );
}

// ─── CALENDARIO MODAL ────────────────────────────────────────────────────
function CalendarModal({ obraId, onClose, mon }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/obras/${obraId}/certificaciones/calendario`, opts)
      .then(({ data }) => setData(data)).catch(() => toast.error("Error cargando calendario"));
  }, [obraId]);
  const certs = data?.certificaciones || [];
  const filas = [
    ["Fecha Cierre", c => c.fecha_cierre ? formatDate(c.fecha_cierre) : "—"],
    ["Fecha Pago", c => c.fecha_pago_estimada ? formatDate(c.fecha_pago_estimada) : "—"],
    ["Estado", c => (ESTADO_BADGE[c.estado]?.label || c.estado)],
    ["Monto", c => mon.fmt(c.monto_total)],   // Fix FG: en la moneda del contrato
    ["Anticipo", c => mon.fmt(c.anticipo_aplicado)],
    ["Retención", c => mon.fmt(c.retencion_fondo_reparo)],
    ["Multas", c => mon.fmt(c.multas)],
    ["Facturado", c => mon.fmt(c.facturado)],
  ];
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-auto" data-testid="cert-calendar-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><CalendarDays size={18} className="text-gray-500" /> Calendario de Certificación</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6">
          {certs.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">No hay certificaciones todavía.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead><tr className="bg-gray-50"><th className="px-3 py-2 text-left font-semibold text-gray-500"></th>
                  {certs.map(c => <th key={c.numero} className="px-3 py-2 text-right font-semibold text-gray-700">Cert. N°{c.numero}</th>)}
                </tr></thead>
                <tbody>
                  {filas.map(([label, fn], i) => (
                    <tr key={label} className={i % 2 ? "bg-gray-50/40" : ""}>
                      <td className="px-3 py-2 font-medium text-gray-500">{label}</td>
                      {certs.map(c => {
                        const isEstado = label === "Estado";
                        const badge = ESTADO_BADGE[c.estado] || { cls: "bg-gray-100 text-gray-600" };
                        return <td key={c.numero} className="px-3 py-2 text-right tabular-nums text-gray-700">
                          {isEstado ? <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>{fn(c)}</span> : fn(c)}
                        </td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

// ─── MAIN TAB ──────────────────────────────────────────────────────────────
export default function CertificacionesTab({ obra, canManage }) {
  const [view, setView] = useState("list");
  const [editingCertId, setEditingCertId] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [acting, setActing] = useState(false);
  // Fix N (2026-08-31): enviar al cliente y devolución del cliente.
  const [enviarTarget, setEnviarTarget] = useState(null);
  const [devolucionTarget, setDevolucionTarget] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [devMonto, setDevMonto] = useState("");
  const [devFecha, setDevFecha] = useState("");
  const [devObs, setDevObs] = useState("");
  // Fix FG (2026-10-08): moneda del contrato de la obra (Gs. o USD con su cambio).
  const [monInfo, setMonInfo] = useState(null);
  const mon = monedaCert(monInfo);
  const [editMon, setEditMon] = useState(false);
  const [monSel, setMonSel] = useState("PYG");
  const [tcSel, setTcSel] = useState("");
  const [guardandoMon, setGuardandoMon] = useState(false);
  const tcHoy = useTipoCambio(null, editMon && monSel === "USD");

  const abrirMoneda = () => {
    setMonSel(mon.usd ? "USD" : "PYG");
    setTcSel(mon.usd ? String(mon.tc) : "");
    setEditMon(true);
  };
  const guardarMoneda = async () => {
    const tc = Number(tcSel) || Number(tcHoy?.usd_pyg) || 0;
    if (monSel === "USD" && !(tc > 0)) { toast.error("Indicá el tipo de cambio del contrato"); return; }
    setGuardandoMon(true);
    try {
      await axios.put(`${API}/obras/${obra._id}`, { moneda: monSel, tipo_cambio_contrato: monSel === "USD" ? tc : null }, opts);
      toast.success("Moneda del contrato actualizada");
      setEditMon(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar la moneda del contrato");
    } finally { setGuardandoMon(false); }
  };

  const enviarCert = async () => {
    setEnviando(true);
    try {
      await axios.post(`${API}/obras/${obra._id}/certificaciones/${enviarTarget._id}/enviar`, {}, opts);
      toast.success("Certificación enviada a Finanzas: queda pendiente de cobro en Tesorería → Cobranzas");
      setEnviarTarget(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "No se pudo enviar"));
    } finally { setEnviando(false); }
  };

  const guardarDevolucion = async () => {
    setEnviando(true);
    try {
      // Fix N (2026-08-31): endpoint propio (no update_certificacion, que bloquea
      // emitida/pagada con 409 y recalcularía montos en cero con un payload parcial).
      await axios.put(`${API}/obras/${obra._id}/certificaciones/${devolucionTarget._id}/devolucion`, {
        monto: mon.aGs(devMonto),   // Fix FG: se carga en la moneda del contrato, se guarda en Gs.
        fecha: devFecha || null,
        observaciones: devObs || "",
      }, opts);
      toast.success("Devolución registrada");
      setDevolucionTarget(null);
      setDevMonto(""); setDevFecha(""); setDevObs("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar la devolución");
    } finally { setEnviando(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obra._id}/certificaciones`, opts)
      .then(({ data }) => { setCerts(data.certificaciones || []); setMonInfo({ moneda: data.moneda, tipo_cambio: data.tipo_cambio }); })
      .catch(() => toast.error("Error cargando certificaciones"))
      .finally(() => setLoading(false));
  }, [obra._id]);
  useEffect(load, [load]);

  const openNew = () => { setEditingCertId(null); setView("editor"); };
  const openEdit = (id) => { setEditingCertId(id); setView("editor"); };
  const onSaved = () => { setView("list"); setEditingCertId(null); load(); };

  // Fix EA (2026-09-23): el cobro se registra y se anula solo en Tesorería → Cobranzas.
  const eliminar = async (id) => {
    try { await axios.delete(`${API}/obras/${obra._id}/certificaciones/${id}`, opts); toast.success("Certificación eliminada"); setDeleteTarget(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const aprobar = async (id) => {
    setActing(true);
    try {
      await axios.post(`${API}/obras/${obra._id}/certificaciones/${id}/aprobar`, {}, opts);
      toast.success("Certificación aprobada — el avance de la obra fue actualizado");
      setApproveTarget(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error al aprobar"));
    } finally { setActing(false); }
  };
  const rechazar = async (id) => {
    if (!rejectComment.trim()) { toast.error("El comentario de rechazo es obligatorio"); return; }
    setActing(true);
    try {
      await axios.post(`${API}/obras/${obra._id}/certificaciones/${id}/rechazar`, { comentario: rejectComment.trim() }, opts);
      toast.success("Certificación rechazada");
      setRejectTarget(null); setRejectComment(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al rechazar"); }
    finally { setActing(false); }
  };
  const exportPdf = async (c) => {
    try { await downloadFile(`${API}/obras/${obra._id}/certificaciones/${c._id}/export-pdf`, `Certificacion_${c.numero}_${obra.codigo}.pdf`); }
    catch { toast.error("Error generando PDF"); }
  };
  const exportPlanilla = async () => {
    try { await downloadFile(`${API}/obras/${obra._id}/certificaciones-planilla`, `Certificaciones_${obra.codigo}.xlsx`); }
    catch { toast.error("Error generando Excel"); }
  };

  if (view === "editor") {
    return <CertEditor obra={obra} certId={editingCertId} onClose={() => { setView("list"); setEditingCertId(null); }} onSaved={onSaved} />;
  }

  return (
    <div className="space-y-4" data-testid="obra-certificaciones-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Certificación de obra al cliente · {certs.filter(c => c.estado === "emitida" || c.estado === "pagada").length} emitida{certs.filter(c => c.estado === "emitida" || c.estado === "pagada").length !== 1 ? "s" : ""}</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowCalendar(true)} className="flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 px-3.5 py-2 rounded-xl text-xs font-semibold" data-testid="cert-calendar-btn"><CalendarDays size={14} /> Ver Calendario</button>
          <button onClick={exportPlanilla} className="flex items-center gap-1.5 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-3.5 py-2 rounded-xl text-xs font-semibold" data-testid="cert-export-planilla-btn"><FileSpreadsheet size={14} /> Exportar Planilla</button>
          {canManage && <button onClick={openNew} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-xl text-xs font-semibold" data-testid="cert-new-btn"><Plus size={14} /> Nueva Certificación</button>}
        </div>
      </div>

      {/* Fix FG (2026-10-08): moneda del contrato. Si no se fijó en la obra, vale la del
          presupuesto de origen. */}
      {monInfo && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="cert-moneda-contrato">
            <span className="text-gray-500">Moneda del contrato:</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${mon.usd ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`} data-testid="cert-moneda-actual">
              {mon.usd ? `Dólares (US$) · ${fmtTC(mon.tc)}` : "Guaraníes (Gs.)"}
            </span>
            {canManage && !editMon && <button onClick={abrirMoneda} className="text-[#EA580C] hover:underline font-semibold" data-testid="cert-moneda-cambiar">Cambiar</button>}
            {canManage && editMon && (
              <span className="flex flex-wrap items-center gap-1.5">
                <select value={monSel} onChange={e => setMonSel(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white" data-testid="cert-moneda-select">
                  <option value="PYG">Guaraníes (Gs.)</option>
                  <option value="USD">Dólares (US$)</option>
                </select>
                {monSel === "USD" && (
                  <input type="number" min={0} step="0.01" value={tcSel} onChange={e => setTcSel(e.target.value)}
                    placeholder={tcHoy ? `${Math.round(tcHoy.usd_pyg)} (hoy)` : "Gs. por 1 USD"}
                    className="w-36 border border-gray-300 rounded-lg px-2 py-1 text-xs text-right" data-testid="cert-moneda-tc" />
                )}
                <button onClick={guardarMoneda} disabled={guardandoMon} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg px-3 py-1 font-semibold disabled:opacity-50" data-testid="cert-moneda-guardar">Guardar</button>
                <button onClick={() => setEditMon(false)} className="border border-gray-300 text-gray-600 rounded-lg px-3 py-1">Cancelar</button>
              </span>
            )}
          </div>
          {canManage && editMon && (
            <p className="text-[11px] text-gray-400">Los montos se siguen guardando en Gs.: en dólares se muestran y se cargan a este cambio. Las cobranzas ya generadas no cambian.</p>
          )}
        </div>
      )}

      {loading ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
        : certs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center shadow-sm">
            <Award size={44} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-600 font-medium">Aún no hay certificaciones</p>
            <p className="text-xs text-gray-400 mt-1">Creá la primera certificación de avance para facturar al cliente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200/60 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-100 text-left">
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-16">N°</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Emisión</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Cierre</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Pago est.</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs text-right">Monto (c/IVA)</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Estado</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs text-right">Acciones</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {certs.map(c => {
                  const badge = ESTADO_BADGE[c.estado] || { label: c.estado, cls: "bg-gray-100 text-gray-600" };
                  const isPendiente = c.estado === "pendiente_aprobacion";
                  const isRechazada = c.estado === "rechazada";
                  const isEmitida = c.estado === "emitida";
                  // Fix N (2026-08-31): el estado "proyectado" ya existía en el backend
                  // y en ESTADO_BADGE, pero nunca se podía llegar a él desde la pantalla.
                  const isProyectado = c.estado === "proyectado";
                  return (
                    <tr key={c._id} className={`hover:bg-gray-50/60 ${isPendiente ? "bg-amber-50/60" : isRechazada ? "bg-red-50/40" : ""}`} data-testid={`cert-row-${c.numero}`}>
                      <td className="px-3 py-2.5 font-bold text-gray-800">{c.numero}</td>
                      <td className="px-3 py-2.5 text-gray-600">{c.fecha_emision ? formatDate(c.fecha_emision) : (c.enviado_at ? formatDate(c.enviado_at) : "—")}</td>
                      <td className="px-3 py-2.5 text-gray-600">{c.fecha_cierre ? formatDate(c.fecha_cierre) : "—"}</td>
                      <td className="px-3 py-2.5 text-gray-600">{c.fecha_pago_estimada ? formatDate(c.fecha_pago_estimada) : "—"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900 tabular-nums" data-testid={`cert-monto-${c.numero}`}>
                        {isPendiente || isRechazada ? "—" : mon.fmt(c.monto_total)}
                        {mon.usd && !(isPendiente || isRechazada) && <p className="text-[10px] font-normal text-gray-400">≈ {formatPYG(c.monto_total)}</p>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${badge.cls}`} title={isRechazada ? (c.comentario_rechazo || "") : (isPendiente ? `Enviado por ${c.enviado_por_name || "?"}` : "")}>{badge.label}</span>
                        {isPendiente && <p className="text-[10px] text-gray-400 mt-0.5">por {c.enviado_por_name || "?"}</p>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {isPendiente && canManage && <button onClick={() => setApproveTarget(c)} title="Aprobar" className="p-1.5 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50" data-testid={`cert-aprobar-${c.numero}`}><Check size={14} /></button>}
                          {isPendiente && canManage && <button onClick={() => setRejectTarget(c)} title="Rechazar" className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`cert-rechazar-${c.numero}`}><Ban size={14} /></button>}
                          {/* Fix N (2026-08-31): un proyectado se edita, se envía y se borra;
                              un emitido (= aprobado para el cliente) es inmutable. El fix J ya
                              devuelve 409 al editarlo o borrarlo, pero los botones seguían en
                              pantalla y el usuario se comía el rechazo. */}
                          {canManage && isProyectado && <button onClick={() => openEdit(c._id)} title="Editar" className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" data-testid={`cert-edit-${c.numero}`}><Pencil size={14} /></button>}
                          {/* Fix EA (2026-09-23): "no se debe permitir marcar como pagado desde el
                              módulo de OBRA (…) Lo que debemos permitir en el modulo OBRA es: editar
                              el certificado si hubieren modificaciones y confirmar el envio a
                              Finanzas donde se verá como pendiente". El cobro se registra y se
                              anula en Tesorería → Cobranzas. */}
                          {canManage && isProyectado && <button onClick={() => setEnviarTarget(c)} title="Confirmar envío a Finanzas" className="p-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50" data-testid={`cert-enviar-${c.numero}`}><Send size={14} /></button>}
                          {canManage && isProyectado && <button onClick={() => setDeleteTarget(c)} title="Eliminar" className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50" data-testid={`cert-delete-${c.numero}`}><Trash2 size={14} /></button>}
                          {canManage && (isEmitida || c.estado === "pagada") && <button onClick={() => setDevolucionTarget(c)} title="Devolución del cliente" className="p-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid={`cert-devolucion-${c.numero}`}><Undo2 size={14} /></button>}
                          {(isEmitida || c.estado === "pagada") && <button onClick={() => exportPdf(c)} title="PDF" className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" data-testid={`cert-pdf-${c.numero}`}><FileText size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {showCalendar && <CalendarModal obraId={obra._id} mon={mon} onClose={() => setShowCalendar(false)} />}

      {/* Fix N (2026-08-31): enviar el proyectado al cliente. El endpoint valida el
          tope de 100% con los datos del momento, que pueden haber cambiado desde
          que se guardó el borrador, y recién ahí lo pasa a emitida. */}
      {enviarTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setEnviarTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cert-enviar-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmar envío a Finanzas</h3>
            <p className="text-sm text-gray-500 mb-5">La certificación N° {enviarTarget.numero} pasa a Tesorería → Cobranzas como pendiente de cobro. El cobro se registra allá, con el comprobante y el N° de factura. Una vez enviada no se puede editar ni eliminar.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEnviarTarget(null)} className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-5 py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={enviarCert} disabled={enviando} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="cert-enviar-confirm">
                {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Confirmar envío
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Fix N (2026-08-31): devolución del cliente — lo que el cliente da por
          válido y paga contra lo que se le certificó. El campo `devolucion` ya se
          persiste en el alta y en la edición; solo faltaba dónde cargarlo. */}
      {devolucionTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDevolucionTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cert-devolucion-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Devolución del cliente</h3>
            <p className="text-xs text-gray-500 mb-4">Certificación N° {devolucionTarget.numero} — certificado {mon.fmt(devolucionTarget.monto_total)}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Monto que el cliente da por válido ({mon.sigla})</label>
                <input type="number" value={devMonto} onChange={e => setDevMonto(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="cert-devolucion-monto" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Fecha</label>
                <input type="date" value={devFecha} onChange={e => setDevFecha(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="cert-devolucion-fecha" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Observaciones</label>
                <textarea value={devObs} onChange={e => setDevObs(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none" data-testid="cert-devolucion-obs" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setDevolucionTarget(null)} className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl px-5 py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={guardarDevolucion} disabled={enviando} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="cert-devolucion-guardar">Guardar</button>
            </div>
          </div>
        </div>, document.body
      )}

      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cert-delete-modal">
            <div className="flex items-center gap-3 mb-3"><div className="p-2.5 rounded-xl bg-red-50 text-red-600"><AlertTriangle size={20} /></div><h2 className="text-lg font-bold text-gray-900">Eliminar Certificación</h2></div>
            <p className="text-sm text-gray-600">¿Eliminar la certificación N° <strong>{deleteTarget.numero}</strong>? Esta acción no se puede deshacer.</p>
            <div className="flex gap-3 pt-5">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => eliminar(deleteTarget._id)} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="cert-delete-confirm">Eliminar</button>
            </div>
          </div>
        </div>, document.body)}

      {approveTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setApproveTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cert-approve-modal">
            <div className="flex items-center gap-3 mb-3"><div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600"><Check size={20} /></div><h2 className="text-lg font-bold text-gray-900">Aprobar Certificación</h2></div>
            <p className="text-sm text-gray-600">Se aprobará el envío de <strong>{approveTarget.enviado_por_name}</strong> (N° {approveTarget.numero}). Los % propuestos se aplicarán al avance oficial de la obra y la certificación pasará a <strong>Emitida</strong>.</p>
            <div className="flex gap-3 pt-5">
              <button onClick={() => setApproveTarget(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => aprobar(approveTarget._id)} disabled={acting} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="cert-approve-confirm">{acting ? "Aprobando..." : "Aprobar"}</button>
            </div>
          </div>
        </div>, document.body)}

      {rejectTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setRejectTarget(null); setRejectComment(""); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cert-reject-modal">
            <div className="flex items-center gap-3 mb-3"><div className="p-2.5 rounded-xl bg-red-50 text-red-600"><Ban size={20} /></div><h2 className="text-lg font-bold text-gray-900">Rechazar Certificación</h2></div>
            <p className="text-sm text-gray-600 mb-3">El envío de <strong>{rejectTarget.enviado_por_name}</strong> (N° {rejectTarget.numero}) volverá al estado <strong>Rechazada</strong>. Los % de la obra no se modifican.</p>
            <label className="block text-xs font-medium text-gray-600 mb-1">Comentario de rechazo *</label>
            <textarea value={rejectComment} onChange={e => setRejectComment(e.target.value)} rows={3} placeholder="Explicá qué debe corregirse..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="cert-reject-comment" />
            <div className="flex gap-3 pt-5">
              <button onClick={() => { setRejectTarget(null); setRejectComment(""); }} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => rechazar(rejectTarget._id)} disabled={acting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="cert-reject-confirm">{acting ? "Rechazando..." : "Rechazar"}</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
